import { useEffect, useState, useCallback } from "react";
import { osFetch } from "./api";
import { useOS } from "./OSContext";
import { Newspaper, Loader2, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function DailyBrief() {
  const { orgId } = useOS();
  const [types, setTypes] = useState([]);
  const [active, setActive] = useState("ceo_morning");
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { osFetch("/brief/types").then(setTypes).catch(() => {}); }, []);

  const loadLatest = useCallback((type) => {
    setLoading(true); setBrief(null);
    osFetch(`/brief/latest?type=${type}`).then((b) => setBrief(b)).catch(() => setBrief(null)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadLatest(active); }, [active, orgId, loadLatest]);

  const generate = async () => {
    setGenerating(true);
    toast.info("ArcaOS is composing your brief…");
    try { const b = await osFetch(`/brief/${active}`, { method: "POST" }); setBrief(b); toast.success("Brief ready"); }
    catch (e) { toast.error(e.message); } finally { setGenerating(false); }
  };

  return (
    <div className="max-w-4xl" data-testid="os-daily-brief">
      <p className="font-mono text-[11px] uppercase tracking-widest text-[#00F0FF] mb-1 flex items-center gap-2"><Newspaper size={13} /> Daily Brief</p>
      <h1 className="font-display font-extrabold text-3xl">Company intelligence, on demand</h1>
      <p className="text-slate-500 mt-1 text-sm">Every brief runs on the same live ArcaOS intelligence — the Attention Center, projects, roadmap, decisions and milestones.</p>

      <div className="flex flex-wrap gap-2 mt-6">
        {types.map((t) => (
          <button key={t.type} onClick={() => setActive(t.type)} className={`text-xs font-medium rounded-full px-4 py-2 border transition-colors ${active === t.type ? "bg-[#0055FF] border-[#0055FF] text-white" : "border-white/10 text-slate-400 hover:text-white"}`} data-testid={`os-brief-type-${t.type}`}>{t.label}</button>
        ))}
      </div>

      <div className="flex items-center justify-between mt-6">
        <p className="text-[11px] font-mono text-slate-500">{brief ? `Generated ${new Date(brief.created_at).toLocaleString()} · ${brief.generated_by_run?.slice(0, 8)}` : "No brief generated yet."}</p>
        <div className="flex gap-2">
          <button onClick={() => loadLatest(active)} className="text-slate-400 hover:text-white border border-white/10 rounded-xl p-2.5" title="Reload latest"><RefreshCw size={15} /></button>
          <button onClick={generate} disabled={generating} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-4 py-2.5 rounded-xl font-semibold hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-50" data-testid="os-brief-generate">{generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {generating ? "Composing…" : "Generate brief"}</button>
        </div>
      </div>

      <div className="mt-6">
        {loading ? <div className="flex items-center gap-2 text-slate-400 py-16"><Loader2 className="animate-spin" /> Loading…</div>
          : !brief ? <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center text-slate-500" data-testid="os-brief-empty">Generate this brief to see the latest intelligence.</div>
          : (
          <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" data-testid="os-brief-content">
            <h2 className="font-display font-extrabold text-2xl">{brief.title}</h2>
            {brief.headline && <p className="text-[#00F0FF] mt-2 text-[15px] leading-snug">{brief.headline}</p>}
            <div className="mt-6 space-y-5">
              {brief.sections?.map((s, i) => (
                <div key={i} data-testid={`os-brief-section-${i}`}>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">{s.title}</p>
                  <p className="text-slate-300 whitespace-pre-wrap text-sm leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
